import { useState } from "react";
import { object } from "prop-types";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  useEstimateEditorStore,
  useEstimateStructure,
  useVisibilityModeCellClassName,
  useWorkTypePermitChecker,
  useEstimateEditorSettings,
  useEstimateTableSettings,
  BreakdownAutomation,
  BreakdownRoundings,
  ValueCell,
  VisibilityButton,
} from "@tools";
import { IconButton, Tooltip, useTheme } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { HeaderWorkTypeActions } from "./HeaderWorkTypeActions";
import { Percent } from "@assets";
import { Cached } from "@material-ui/icons";

export const HeaderWorkType = observer(({
  workType,
  section,
  extendPadding,
  ...cellProps
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isLibrary, allowEdition } = useEstimateEditorSettings();
  const { visibilityMode } = useEstimateEditorStore();
  const { workTypeColumnsMaxState } = useEstimateTableSettings();
  const structure = useEstimateStructure();
  
  const { editionGrant, isPending, hasPermit, userSeesOnlyPermittedWorkTypes } = useWorkTypePermitChecker(
    workType.id
  );
  
  const [ automationModalVisible, openAutomationModal ] = useState(false);
  const [ roundingsModalVisible, openRoundingsModal ] = useState(false);
  
  const { isHidden } = workType;
  const visible = !structure.isWorkTypeOff(workType.id);
  
  const className = useVisibilityModeCellClassName(isHidden);

  return (
    <ValueCell
      key={workType.id}
      isHeader
      size={workTypeColumnsMaxState[workType.id] ? "medium" : "small"}
      style={{
        cursor: allowEdition && "grab",
        color:
          allowEdition &&
          hasPermit &&
          (isPending ? theme.props.warning : theme.props.success),
      }}
      extendPadding={extendPadding || allowEdition || visibilityMode}
      visible={visible && editionGrant}
      className={className}
      {...cellProps}
    >
      <Tooltip
        title={t(`views.editor.status_${isPending ? "pending" : "done"}`)}
        disableHoverListener={!allowEdition || !hasPermit}
      >
        <span className="preset-tableBreakdownHeader">
          { workType?.name || t("common.unnamed") }
        </span>
      </Tooltip>
      
      {
        allowEdition && (workType.hasPercent || workType.hasRounding) &&
        <Grid
          item container
          justifyContent="center"
          alignItems="center"
          className="absolute w-max"
          style={{ top: -16 }}
        >
          {
            workType.hasPercent &&
            <IconButton
              size="small"
              color="primary"
              className="text-sm"
              onClick={() => openAutomationModal(!automationModalVisible)}
            >
              <Percent />
            </IconButton>
          }
          {
            workType.hasRounding &&
            <IconButton
              size="small"
              color="primary"
              className="text-sm"
              onClick={() => openRoundingsModal(!roundingsModalVisible)}
            >
              <Cached />
            </IconButton>
          }
        </Grid>
      }
      {
        allowEdition &&
        <HeaderWorkTypeActions
          workType={workType}
          section={section}
          hasPermit={hasPermit && editionGrant}
          isPending={isPending}
          userSeesOnlyPermittedWorkTypes={userSeesOnlyPermittedWorkTypes}
          openWorkTypeAutomationDialog={openAutomationModal}
          openWorkTypeRoundingsDialog={openRoundingsModal}
        />
      }
      {
        visibilityMode &&
        <VisibilityButton
          size="tiny"
          inColumn right={-16}
          hidden={isHidden}
          onChange={workType.setHideWorkType}
        />
      }
      {
        automationModalVisible && !isLibrary &&
        <BreakdownAutomation
          open
          workType={workType}
          section={section}
          onClose={() => openAutomationModal(false)}
        />
      }
      {
        roundingsModalVisible && !isLibrary &&
        <BreakdownRoundings
          open
          workType={workType}
          section={section}
          onClose={() => openRoundingsModal(false)}
        />
      }
    </ValueCell>
  );
});

HeaderWorkType.propTypes = {
  workType: object.isRequired,
  section: object.isRequired,
};
